/**
 * ISSUES. The damage protocol as a list. Office only.
 *
 * Damage and breakage are told to the client the same day, by us, and an issue of either kind with
 * no `clientToldAt` is THE gap on this screen: it is the coral pill, it is the first group in the
 * list, and it stays there until somebody ticks "I told the client", which stamps the moment.
 *
 * `discoveredByClient` is the honest fact beside it and it is drawn quietly, because coral here
 * means a gap somebody can close and who found the damage is not one: it will be true forever. The
 * damage a business survives is the damage it finds and reports itself, and that is an argument
 * for showing the fact on every row, not for painting it the colour of unfinished work.
 *
 * An issue moves open, acknowledged, put right, closed, and the server refuses a step backwards.
 * The remedy and its cost are recorded against the issue; the cost is a figure typed in dollars,
 * and it is what a repair cost us, never a price.
 *
 * ON A DESK THE LIST IS A TABLE AND THE WORK IS A PANEL. A phone opens the controls under the card
 * they belong to, because a sheet over the row hides the thing it is about; a desk has room for
 * the row and the form side by side, so the panel is the same form and the cards are not drawn at
 * all. Both shapes ask for the same `MoveAlong`, so the rule that a tick rides on whatever is
 * pressed next is written once, and each copy names its controls after where it is drawn.
 *
 * AND WHAT THE CLIENT READS OF IT (ops #98, `docs/plan-client-language.md` sections 4 and 7). The line
 * a cleaner wrote never reaches the client: the office writes the client's own, in the household's
 * language, beside it, and only that line reaches the household's journal at home. The panel names the
 * language, shows the line as written, starts the box empty or holding the line already written, and
 * fills nothing on its own: for an English household one press copies the summary in for the office to
 * send or change, and for one that reads another language no copy is offered. On an
 * issue of kind `other` it offers "Already there", named with the phrase the household's language
 * takes, which the line must then open with.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  ALREADY_THERE,
  ISSUE_STATES,
  REMEDIES,
  opensAlreadyThere,
  type IssueState,
  type Remedy,
} from '@fk/shared';
import {
  ApiError,
  listIssues,
  patchIssue,
  writeIssueForClient,
  type IssuePatch,
  type OfficeIssueRow,
} from '../api.ts';
import { Card, Detail, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import {
  DataTable,
  FilterBar,
  FilterSelect,
  PanelRule,
  SidePanel,
  SplitLayout,
  useWideView,
  type DataColumn,
  type RowGroup,
} from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { GapPill } from '../components/StatusPills.tsx';
import { CheckField } from '../components/VisitBits.tsx';
import { WordsCheck, refusedFieldsOf, useRefusedBoxes } from '../components/WordsCheck.tsx';
import { centsFromDollars, dollarsForInput, formatInstant, money, shortDay } from '../format.ts';
import {
  CLIENT_LANGUAGE_LABELS,
  ISSUE_KIND_LABELS,
  ISSUE_STATE_LABELS,
  REMEDY_LABELS,
} from '../labels.ts';

const MUST_TELL = new Set(['damage', 'breakage']);

/** The one gap on this screen: damage or breakage the client has not been told about. */
function untold(issue: OfficeIssueRow): boolean {
  return MUST_TELL.has(issue.kind) && issue.clientToldAt === null;
}

export function Issues() {
  const wide = useWideView();
  const [state, setState] = useState<IssueState | ''>('open');
  const [rows, setRows] = useState<readonly OfficeIssueRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    setRows(null);
    listIssues(state)
      .then((answer) => {
        if (live) setRows(answer.issues);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The issues did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [state, attempt]);

  const changed = (sentence: string) => {
    setSaid(sentence);
    reload();
  };

  const waiting = (rows ?? []).filter(untold);
  const settled = (rows ?? []).filter((issue) => !untold(issue));
  const groups: readonly RowGroup<OfficeIssueRow>[] =
    waiting.length === 0
      ? [{ key: 'all', rows: rows ?? [] }]
      : [
          { key: 'untold', caption: 'The client has not been told', rows: waiting },
          { key: 'rest', caption: 'Everything else', rows: settled },
        ].filter((group) => group.rows.length > 0);

  const picked = (rows ?? []).find((issue) => issue.id === pickedId) ?? null;
  const panel =
    wide && picked ? (
      <SidePanel
        key={picked.id}
        title={picked.clientName}
        pills={<IssuePills issue={picked} />}
        onClose={() => setPickedId(null)}
      >
        <p className="font-serif text-lg leading-tight">{ISSUE_KIND_LABELS[picked.kind]}</p>
        <IssueFacts issue={picked} />
        <PanelRule />
        <ForTheClient
          key={`${picked.id}-${picked.forClientAt ?? ''}`}
          idPrefix="panel"
          issue={picked}
          onChanged={changed}
        />
        {picked.state !== 'closed' ? (
          <>
            <PanelRule />
            <h3 className="font-serif text-lg leading-7">Move this along</h3>
            <MoveAlong
              key={`${picked.id}-${picked.state}`}
              idPrefix="panel"
              issue={picked}
              onChanged={changed}
            />
          </>
        ) : null}
      </SidePanel>
    ) : undefined;

  return (
    <div>
      <Link
        to="/breaches"
        className="mb-3 inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium text-navy/70"
      >
        Back to promises
      </Link>
      <ScreenTitle title="Issues" subtitle="What went wrong, and what was done about it." />

      <FilterBar>
        <FilterSelect
          id="issue-state"
          label="Showing"
          value={state}
          onChange={setState}
          options={ISSUE_STATES}
          toLabel={(option) => ISSUE_STATE_LABELS[option]}
          anyLabel="Everything"
          className="md:w-48"
        />
      </FilterBar>

      {said ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      {error ? (
        <ErrorNotice title="The issues did not load" message={error.message} onRetry={reload} />
      ) : rows === null ? (
        <Busy what="the issues" />
      ) : rows.length === 0 ? (
        <EmptyNotice title={state === 'open' ? 'Nothing open' : 'Nothing here'}>
          <p>
            {state === 'open'
              ? 'No issue is waiting on anybody. A cleaner records one from the job sheet.'
              : 'No issue is in that state.'}
          </p>
        </EmptyNotice>
      ) : (
        <SplitLayout panel={panel}>
          <p className={`mb-2 hidden text-xs text-navy/60 md:block ${MEASURE}`}>
            The ones the client has not been told about come first. Pick an issue to move it along
            in the panel it opens.
          </p>
          <DataTable
            label="Issues"
            groups={groups}
            columns={issueColumns(setPickedId)}
            rowKey={(issue) => issue.id}
            rowClassName={(issue) =>
              issue.id === pickedId
                ? 'bg-gold/10 [&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-gold [&>td:first-child]:pl-[13px]'
                : ''
            }
            card={(issue) => <IssueCard issue={issue} onChanged={changed} actions={!wide} />}
          />
        </SplitLayout>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The table, at md and above. The same rows the cards are drawn from.
 * ------------------------------------------------------------------ */

/**
 * The household and the visit are words here, not links: picking the row opens the panel, and the
 * panel carries both links at 44px each. A 12px link in a table cell is a target nobody can hit.
 *
 * THE WIDTHS ARE SHARES, NOT PIXELS, and that is the artboard's proportions rather than its
 * measurements. A fixed table layout takes the greater of its own width and the sum of its
 * columns, so 176 plus 140 plus 188 is 504px of table inside a track that is 308px wide when the
 * panel is open at 1024, and the wrapper clipped rather than scrolled: the coral pill this screen
 * exists to show ended up off the right edge with no way to reach it. A percentage always resolves
 * against the track, so the table is as wide as the room it has, and since ops #56 it scrolls in
 * its own frame below the floor `tableMinWidth` sets rather than cutting anything.
 */
function issueColumns(pick: (id: string) => void): readonly DataColumn<OfficeIssueRow>[] {
  return [
    {
      key: 'household',
      header: 'Household',
      width: '24%',
      cell: (issue) => (
        <>
          <span className="block font-semibold">{issue.clientName}</span>
          <span className="block text-xs text-navy/60">Visit {shortDay(issue.visitDate)}</span>
        </>
      ),
    },
    {
      key: 'what',
      header: 'What happened',
      cell: (issue) => (
        <button
          type="button"
          onClick={() => pick(issue.id)}
          className="flex min-h-11 w-full flex-col justify-center text-left desk:min-h-9"
        >
          <span className="font-medium">{ISSUE_KIND_LABELS[issue.kind]}</span>
          <span className="text-xs break-words text-navy/70">{issue.summary}</span>
        </button>
      ),
    },
    {
      key: 'state',
      header: 'State',
      width: '19%',
      cell: (issue) => (
        <Pill tone={issue.state === 'closed' ? 'quiet' : 'gold'}>
          {ISSUE_STATE_LABELS[issue.state]}
        </Pill>
      ),
    },
    {
      key: 'gap',
      header: 'Where it stands',
      width: '26%',
      // The widest pill, "Discovered by the client", does not wrap, so the column keeps its
      // width and the table scrolls in its frame below it rather than the pill leaving its cell.
      minWidth: '168px',
      cell: (issue) => (
        <div className="flex flex-wrap gap-1.5">
          {untold(issue) ? (
            <GapPill>Client not told yet</GapPill>
          ) : issue.clientToldAt ? (
            <Pill tone="sage">Client told</Pill>
          ) : null}
          {issue.discoveredByClient ? <Pill>Discovered by the client</Pill> : null}
        </div>
      ),
    },
  ];
}

/* ------------------------------------------------------------------ *
 * The card, below md, and the parts both shapes share.
 * ------------------------------------------------------------------ */

function IssuePills({ issue }: { issue: OfficeIssueRow }) {
  return (
    <>
      <Pill tone={issue.state === 'closed' ? 'quiet' : 'gold'}>
        {ISSUE_STATE_LABELS[issue.state]}
      </Pill>
      {issue.discoveredByClient ? <Pill>Discovered by the client</Pill> : null}
      {untold(issue) ? <GapPill>Client not told yet</GapPill> : null}
    </>
  );
}

function IssueFacts({ issue }: { issue: OfficeIssueRow }) {
  return (
    <div>
      <p className="mt-2 text-sm font-medium">{issue.summary}</p>
      {issue.detail ? <p className="mt-1 text-sm whitespace-pre-line">{issue.detail}</p> : null}

      <div className="mt-2 flex flex-wrap gap-3 text-sm">
        <Link
          to={`/clients/${issue.clientId}`}
          className="inline-flex min-h-11 desk:min-h-9 items-center font-medium underline"
        >
          {issue.clientName}
        </Link>
        <Link
          to={`/visits/${issue.visitId}`}
          className="inline-flex min-h-11 desk:min-h-9 items-center font-medium underline"
        >
          Visit on {shortDay(issue.visitDate)}
        </Link>
      </div>

      <dl className="mt-1">
        <Detail label="Reported" value={formatInstant(issue.reportedAt)} />
        <Detail label="Client told" value={formatInstant(issue.clientToldAt)} />
        <Detail label="Remedy" value={issue.remedy ? REMEDY_LABELS[issue.remedy] : null} />
        <Detail
          label="What it cost us"
          value={issue.remedyCostCents !== null ? money(issue.remedyCostCents) : null}
        />
        <Detail label="Put right" value={formatInstant(issue.remediedAt)} />
        <Detail label="Closed" value={formatInstant(issue.closedAt)} />
      </dl>
    </div>
  );
}

function IssueCard({
  issue,
  onChanged,
  actions,
}: {
  issue: OfficeIssueRow;
  onChanged: (sentence: string) => void;
  /**
   * False when the panel beside the table is drawing the same form. The card list is display:none
   * from md up rather than unmounted, so a card that kept its form would put a second copy of
   * every control in the document, and a label resolves to the FIRST id it matches: the panel's
   * sentences would drive the hidden card's boxes, and the tick that stamps "I told the client"
   * would never reach the patch.
   */
  actions: boolean;
}) {
  return (
    <Card className={untold(issue) ? 'border-coral/40' : ''}>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-serif text-lg leading-tight">{ISSUE_KIND_LABELS[issue.kind]}</h2>
        <IssuePills issue={issue} />
      </div>
      <IssueFacts issue={issue} />

      {actions ? (
        <details className="mt-3 rounded-lg border border-navy/15 p-3">
          <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
            What the client reads
          </summary>
          <ForTheClient
            key={`${issue.id}-${issue.forClientAt ?? ''}`}
            idPrefix="card"
            issue={issue}
            onChanged={onChanged}
          />
        </details>
      ) : null}

      {actions && issue.state !== 'closed' ? (
        <details className="mt-3 rounded-lg border border-navy/15 p-3">
          <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
            Move this along
          </summary>
          <MoveAlong
            key={`${issue.id}-${issue.state}`}
            idPrefix="card"
            issue={issue}
            onChanged={onChanged}
          />
        </details>
      ) : null}
    </Card>
  );
}

function MoveAlong({
  idPrefix,
  issue,
  onChanged,
}: {
  /** Where this copy is drawn, because the card and the panel can both be in the document. */
  idPrefix: string;
  issue: OfficeIssueRow;
  onChanged: (sentence: string) => void;
}) {
  const field = (name: string) => `${idPrefix}-${name}-${issue.id}`;
  const mustTell = MUST_TELL.has(issue.kind);
  const [toldClient, setToldClient] = useState(false);
  const [remedy, setRemedy] = useState<Remedy | ''>(issue.remedy ?? '');
  const [costDollars, setCostDollars] = useState(
    issue.remedyCostCents !== null ? dollarsForInput(issue.remedyCostCents) : '',
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onChanged(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * The tick rides on whatever is pressed next. A person who ticks "I told the client" and then
   * marks the issue put right, or closes it, has said two things, and both go on the record: a
   * patch that dropped the tick would leave the coral "not told" pill on an issue she did tell.
   * The server stamps the moment from its own clock; this screen never reads one.
   */
  const told = (): Pick<IssuePatch, 'clientTold'> =>
    mustTell && toldClient && issue.clientToldAt === null ? { clientTold: true } : {};

  function acknowledge() {
    void run(() => patchIssue(issue.id, { state: 'acknowledged', ...told() }), 'Acknowledged.');
  }

  function recordRemedy() {
    if (!remedy) {
      setProblem('Choose the remedy first.');
      return;
    }
    const cents = costDollars.trim() ? centsFromDollars(costDollars) : null;
    if (costDollars.trim() && cents === null) {
      setProblem('The cost is not an amount. Dollars, like 40 or 40.50, or leave it blank.');
      return;
    }
    void run(
      () => patchIssue(issue.id, { remedy, remedyCostCents: cents, ...told() }),
      'The remedy is on the record.',
    );
  }

  return (
    <div>
      {problem ? (
        <div className="mt-2">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      {mustTell && issue.clientToldAt === null ? (
        <div className="mt-3">
          <CheckField
            id={field('told')}
            label="I told the client"
            checked={toldClient}
            onChange={setToldClient}
            hint="Damage is told the same day, by us. Tick it and whatever you press next stamps the moment."
          />
          {issue.state !== 'open' ? (
            <Button
              tone="quiet"
              disabled={busy || !toldClient}
              onClick={() =>
                run(
                  () => patchIssue(issue.id, { clientTold: true }),
                  'Recorded that they were told.',
                )
              }
            >
              Record that they were told
            </Button>
          ) : null}
        </div>
      ) : null}

      {issue.state === 'open' ? (
        <div className="mt-3">
          <Button onClick={acknowledge} disabled={busy}>
            {busy ? 'Recording' : 'Acknowledge'}
          </Button>
        </div>
      ) : null}

      {issue.state === 'open' || issue.state === 'acknowledged' ? (
        <div className="mt-4 border-t border-navy/10 pt-3">
          <SelectField
            id={field('remedy')}
            label="The remedy"
            value={remedy}
            onChange={setRemedy}
            options={REMEDIES}
            toLabel={(option) => REMEDY_LABELS[option]}
            placeholder="Choose one"
            hint="The published policy: a free re-clean or a full refund, and the choice is theirs."
          />
          <TextField
            id={field('cost')}
            label="What it cost us"
            inputMode="numeric"
            value={costDollars}
            onChange={setCostDollars}
            hint="Dollars. A repair or a replacement, out of our pocket. Blank if nothing."
          />
          <div className="flex flex-wrap gap-2">
            <Button tone="quiet" onClick={recordRemedy} disabled={busy}>
              Record the remedy
            </Button>
            <Button
              disabled={busy}
              onClick={() =>
                run(() => patchIssue(issue.id, { state: 'remedied', ...told() }), 'Put right.')
              }
            >
              Mark it put right
            </Button>
          </div>
        </div>
      ) : null}

      <div className="mt-4 border-t border-navy/10 pt-3">
        <Button
          tone="quiet"
          disabled={busy}
          onClick={() => run(() => patchIssue(issue.id, { state: 'closed', ...told() }), 'Closed.')}
        >
          Close it
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * What the client reads (ops #98).
 * ------------------------------------------------------------------ */

/**
 * THE OFFICE'S LINE FOR THE CLIENT ABOUT AN ISSUE (ops #98, business #49 and #47,
 * `docs/plan-client-language.md` sections 4 and 7). The business's rule 6: nothing a cleaner writes
 * reaches a client until a person in the office has read it and put it into the client's language in
 * their own words. So the line as written is shown, the household's language named, and the box starts
 * empty or holding the line already written. For an English household "Use these words as the
 * client's" copies the summary in, one press for a line the office would write the same, and nothing is
 * saved until the office saves it. For a household that reads another language no copy is offered,
 * since a line as written in English, or in whatever the cleaner's app speaks, is not the client's line,
 * and the panel says the office writes it in that language in its own words (the plan, section 4: the
 * copy is the English household's one press). The tick "Already there" is drawn on an issue of kind `other` alone, started
 * from the mark the office set or, where it has set none, from whether the line as written opens with
 * the English phrase, a convenience and never the fact; the phrase the household's language takes is
 * named beside it, and the server refuses a marked line that does not open with it. The box shows, as
 * the office types, the claims and cuts the server would refuse, never a figure, as the summary's does.
 */
function ForTheClient({
  idPrefix,
  issue,
  onChanged,
}: {
  /** Where this copy is drawn, because the card and the panel can both be in the document. */
  idPrefix: string;
  issue: OfficeIssueRow;
  onChanged: (sentence: string) => void;
}) {
  const field = (name: string) => `${idPrefix}-${name}-${issue.id}`;
  const language = issue.householdLanguage ?? 'en';
  const other = issue.kind === 'other';
  const [summary, setSummary] = useState(issue.forClientSummary ?? '');
  const [alreadyThere, setAlreadyThere] = useState(
    issue.alreadyThere ?? (other && opensAlreadyThere('en', issue.summary)),
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const boxes = useRefusedBoxes();
  const written = Boolean(issue.forClientAt);
  const stale =
    written && issue.forClientLanguage && issue.forClientLanguage !== language
      ? issue.forClientLanguage
      : null;

  async function save(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    boxes.setRefused({});
    if (!summary.trim()) {
      setProblem('Write the line as the client will read it.');
      return;
    }
    setBusy(true);
    try {
      await writeIssueForClient(issue.id, {
        summary: summary.trim(),
        ...(other ? { alreadyThere } : {}),
      });
      onChanged("Saved. The client reads this line at home, in place of the cleaner's.");
    } catch (thrown) {
      const refused = thrown instanceof ApiError ? refusedFieldsOf(thrown.body) : {};
      if (Object.keys(refused).length > 0) boxes.setRefused(refused);
      else setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-issue-for-client className="mt-3">
      <h3 className="font-serif text-lg leading-7">What the client reads</h3>
      <p className={`mt-1 text-sm text-navy/70 ${MEASURE}`}>
        This household reads {CLIENT_LANGUAGE_LABELS[language]}. Read the line as written and write
        it for the client in your own words; it reaches the client only once it is saved here.
      </p>
      {written ? (
        <p className="mt-1 text-xs text-navy/60">
          Written {formatInstant(issue.forClientAt ?? null)}
          {issue.forClientLanguage ? ` in ${CLIENT_LANGUAGE_LABELS[issue.forClientLanguage]}` : ''}.
        </p>
      ) : (
        <p className="mt-1 text-xs text-coral">No words for the client yet.</p>
      )}
      {stale ? (
        <p className="mt-1 text-sm text-coral">
          Written in {CLIENT_LANGUAGE_LABELS[stale]}; this household reads{' '}
          {CLIENT_LANGUAGE_LABELS[language]} now.
        </p>
      ) : null}
      <form className="mt-3" onSubmit={save} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        <p className="mb-1 text-xs break-words text-navy/60">As written: {issue.summary}</p>
        {language === 'en' ? (
          <div className="mb-3">
            <Button
              tone="quiet"
              disabled={busy}
              onClick={() => {
                setSummary(issue.summary);
                boxes.clear('summary');
              }}
            >
              Use these words as the client&apos;s
            </Button>
          </div>
        ) : (
          <p className={`mb-3 text-sm text-navy/70 ${MEASURE}`}>
            Write this line in {CLIENT_LANGUAGE_LABELS[language]}, in your own words: the line as
            written is not copied.
          </p>
        )}
        <TextAreaField
          id={field('for-client')}
          label="The line, for the client"
          value={summary}
          onChange={(value) => {
            setSummary(value);
            boxes.clear('summary');
          }}
          rows={2}
        />
        <WordsCheck
          words={summary}
          refused={boxes.refused.summary}
          refusedTitle="Not saved. The cleaner's words stand as they were. Change the words marked here and save it again."
          figuresPass
        />
        {other ? (
          <CheckField
            id={field('already-there')}
            label="Already there"
            checked={alreadyThere}
            onChange={setAlreadyThere}
            hint={`Damage that was there before the clean. The line then opens with "${ALREADY_THERE[language]}", as this household reads it.`}
          />
        ) : null}
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving' : 'Save what the client reads'}
        </Button>
      </form>
    </div>
  );
}
